/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import { ROUTES, TUTORIAL_IDS } from "@/shared";
import useResponse from "@/shared/hooks/useResponse";
import { useLoginDataStore } from "@/stores/useLoginDataStore";
import { resetAllTablesExceptPaginationAndColumns } from "@/stores/useTableStore";
import Typography from "@mui/material/Typography";

import {
  FileDownloadIcon,
  LanguageIcon,
  LockOutlineIcon,
  LogoutIcon,
  MonitorCursorIcon,
  ToggleOffIcon
} from "@dotnet/telematics-design-system";

import GeneralSettingsIcon from "../../../../../../assets/customIcons/GeneralSettingsIcon";
import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import Avatar from "../../../../../../shared/components/avatars/Avatar";
import { useReleaseFeatureToggle } from "../../../../../../shared/featureToggles/hooks";
import { FEATURE_NAMES } from "../../../../../../shared/featureToggles/types";
import ListChoice from "../../../../../ListChoice/ListChoice";
import Separator from "../../../../../Separator/Separator";
import { useStyles } from "../../../../styles";
import { DIALOG_NAMES, SETTINGS_CONTENT_NAMES } from "../../../../types/enums";
import { ISettingsListItem } from "../../../../types/interfaces";

interface ISettingsInitialContentProps {
  isMobile: boolean;
  setSettingsContent: (newContent: SETTINGS_CONTENT_NAMES) => void;
  closeMenu?: () => void;
  openDialog: (name: DIALOG_NAMES) => void;
}

const SettingsInitialContent = ({
  isMobile,
  setSettingsContent,
  closeMenu,
  openDialog
}: ISettingsInitialContentProps) => {
  const {
    isReleaseFeatureEnabled,
    isReleaseFeatureEnabledForRole,
    isReleaseFeatureEnabledForUser
  } = useReleaseFeatureToggle();
  const navigate = useNavigate();
  const { user, setIsLoading, setUser, setTokenInfo } = useAuth();

  const { handleCommonError } = useResponse();

  const { postData } = useAxiosPrivate();

  const { t } = useTranslation();

  const setIs2FaVerification = useLoginDataStore(
    state => state.setIs2FaVerification
  );

  const { settingsContentTop, settingsContentTopData } = useStyles();

  const handleItemClick = (settingsContent: SETTINGS_CONTENT_NAMES) => {
    setSettingsContent(settingsContent);
  };

  const handleLogOut = async () => {
    try {
      setIsLoading(true);
      await postData(ApiResources.Logout);
      closeMenu?.();
      setTimeout(() => {
        setIsLoading(false);
        setTokenInfo(null);
        setUser(null);

        resetAllTablesExceptPaginationAndColumns();

        setIs2FaVerification(false);
        navigate(ROUTES.Home);
      }, 500);
    } catch (error) {
      setIsLoading(false);
      handleCommonError(error, t);
    }
  };

  const settingsItemList: ISettingsListItem[] = [
    {
      title: t("Settings##general settings"),
      icon: <GeneralSettingsIcon />,
      action: () => openDialog(DIALOG_NAMES.GeneralSettings),
      enabled: true
    },
    {
      title: t("Languages##language"),
      icon: <LanguageIcon />,
      action: () => handleItemClick(SETTINGS_CONTENT_NAMES.Language),
      enabled: true
    },
    {
      title: t("Settings##security"),
      icon: <LockOutlineIcon />,
      action: () => openDialog(DIALOG_NAMES.Security),
      enabled: isReleaseFeatureEnabled(FEATURE_NAMES.ShowSecuritySettings)
    },
    {
      title: t("Settings##tutorials"),
      icon: <MonitorCursorIcon />,
      action: () => handleItemClick(SETTINGS_CONTENT_NAMES.Tutorials),
      enabled: isReleaseFeatureEnabledForRole(
        FEATURE_NAMES.ShowTutorials,
        user?.role
      )
    },
    {
      title: t("Settings##company card authenticator"),
      icon: <FileDownloadIcon />,
      action: () => openDialog(DIALOG_NAMES.CompanyCardAuth),
      id: TUTORIAL_IDS.CompanyCardAuthButton,

      enabled:
        isReleaseFeatureEnabled(FEATURE_NAMES.CcaFileDownload) &&
        isReleaseFeatureEnabledForRole(
          FEATURE_NAMES.CcaFileDownload,
          user?.role
        ),
      actionDisabled: !isReleaseFeatureEnabledForRole(
        FEATURE_NAMES.CcaFileDownload,
        user?.role
      )
    },
    {
      title: t("Settings##feature switches"),
      icon: <ToggleOffIcon />,
      action: () => openDialog(DIALOG_NAMES.FeatureSwitches),
      id: "featureSwitchesButton",
      enabled: isReleaseFeatureEnabledForUser(
        FEATURE_NAMES.AllowFeatureSwitches,
        user?.email
      )
    }
  ];

  const version = import.meta.env.VITE_APP_VERSION ?? undefined;

  return (
    <>
      {!isMobile && user && (
        <>
          <div css={css(settingsContentTop)}>
            <Avatar
              textToCut={user?.email}
              customStyle={{ marginRight: "8px" }}
            />
            <div css={css(settingsContentTopData)}>
              <Typography variant="body3Medium">{user?.email}</Typography>
              <Typography variant="body3Regular">
                {user?.company.name}
              </Typography>
            </div>
          </div>
          <Separator />
        </>
      )}
      {settingsItemList.map(
        (item: ISettingsListItem) =>
          item.enabled && (
            <ListChoice
              id={item?.id}
              key={item.title}
              title={item.title}
              description={item.description}
              iconLeft={item.icon}
              onClick={item.action}
              isLoading={item.isLoading}
              disabled={item.actionDisabled}
            />
          )
      )}

      <Separator />
      <ListChoice
        title={t("Auth##sign out")}
        iconLeft={<LogoutIcon />}
        onClick={handleLogOut}
      />

      {version && (
        <>
          <Separator />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              margin: "12px 16px"
            }}
          >
            <Typography variant="body4Regular" component="div">
              {t("Settings##version")}: {version}
            </Typography>
          </div>
        </>
      )}
    </>
  );
};

export default SettingsInitialContent;
